import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationStart, Params, Router } from '@angular/router';
import { Profile } from '@common/models/profile.model';
import {
    HttpClient,
    HttpContext,
    HttpContextToken,
} from '@angular/common/http';
import { API_BASE_URL } from '@constants/endpoints.constants';
import {
    BehaviorSubject,
    Observable,
    Subject,
    filter,
    first,
    map,
    take,
} from 'rxjs';
import { PAGES_URLS, PAGES } from '@constants/pages-config.constants';
import { SelectedAppliance, Room } from '@common/models/rooms.model';
import { SKIP_SPINNER } from './interceptor.service';
import { OrderParameter } from '@common/models/order-type.model';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class StoreDataService {
    private profile: BehaviorSubject<Profile> = new BehaviorSubject<Profile>({
        //profileId: null,
        firstName: null,
        lastName: null,
        address: null,
        city: null,
        state: null,
        suite: null,
        email: null,
        phone: null,
        locationPhone: null,
        locationState: null,
        locationEmail: null,
        locationName: null,
        postalCode: null,
        updatedAt: null,
        createdAt: null,
        contactPreference: null,
        cleaningOccasion: null,
        cleaningFrequency: null,
        square: null,
        url: null,
        locationCode: null,
        petsNumber: null,
        levels: null,
        numberOfBedrooms: null,
        numberOfBathrooms: null,
        numberOfHalfbathrooms: null,
        rooms: null,
        otherRooms: null,
        subscriptionType: null,
        concerns: null,
        appliances: null,
        scheduling: null,
        termsAccepted: false,
        marketingConsent: false,
        firstCleanPrice: null,
        recurringCleanPrice: null,
        country: null,
        localSiteUrl: null,
        privacyPolicyUrl: null,
        termsOfUseUrl: null,
        selectedStartDate: null,
        initialUrl: null,
        grossCapacity: null,
        insertId: null,
        offerCode: null,
        websiteOfferName: null,
        gclid: null,
        msclkid: null,
        utmsource: null,
        utmmedium: null,
        utmcampaign: null,
        utmterm: null,
        utmmatchtype: null,
        gtc: null,
        utmcontent: null,
        utmadset: null,
    });
    routeToContinueBooking = new Subject<string | undefined>();
    appStarted = false;
    params: any;
    userInfo: Profile = {};
    continueBooking = false;
    initCompleted = false;
    validationPropertyExceptions: string[] = ['cleaningOccasion', 'appliances'];
    initialUrl = '';

    storedProfile: Profile = {};

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private http: HttpClient
    ) {
        this.profileObserver()
            .pipe(take(2))
            .subscribe((profile) => {
                this.storedProfile = profile;
            });
    }

    get profileData(): Profile {
        return this.profile.value;
    }
    set profileData(data: Profile) {
        this.profile.next({
            ...this.profile.value,
            ...data,
        });
    }
    public profileObserver(): Observable<Profile> {
        return this.profile.asObservable();
    }

    setupInitialData() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                if (!this.profileData.profileId && !this.initialUrl) {
                    this.initialUrl = event.url;
                }

                if (this.isRootUrl(event.url)) {
                    return;
                }

                this.route.queryParams.subscribe((params) => {
                    if (Object.keys(params).length > 0) {
                        this.params = params;
                        if (params['continueBooking']) {
                            this.continueBooking = !!params['continueBooking'];
                        }
                        if (!this.initCompleted) {
                            this.initCompleted = true;
                            if (params['profileId']) {
                                this.getProfile(params['profileId']);
                            } else {
                                this.storeInitialData();
                            }
                        }
                    }
                });
            });
    }

    public redirectToNewSiteIfNeeded(currentUrl: string, params: Params): boolean {
        const redirectBaseUrl = environment.redirectNewSiteLink;
        const locationCode = params['locationCode'];
        const redirectLocationCodes =
            environment.redirectToNewSiteLocationCodes || [];

        if (!redirectBaseUrl || locationCode == null) {
            return false;
        }

        const shouldRedirect = redirectLocationCodes.includes(
            String(locationCode)
        );

        if (!shouldRedirect) {
            return false;
        }

        const storageKey = `mm_redirect_ab_${locationCode}`;
        let abAssignment = localStorage.getItem(storageKey);

        if (abAssignment == null) {
            const percentage = environment.redirectNewSitePercentage ?? 100;
            const randomPercentage = Math.random() * 100;

            this.logRedirectRandomPercentage(randomPercentage);

            abAssignment = randomPercentage < percentage ? 'new' : 'old';
            localStorage.setItem(storageKey, abAssignment);
        }

        if (abAssignment === 'old') {
            return false;
        }

        const queryString = this.getQueryString(currentUrl);
        const redirectUrl = queryString
            ? `${redirectBaseUrl}${redirectBaseUrl.includes('?') ? '&' : '?'}${queryString}`
            : redirectBaseUrl;

        this.performExternalRedirect(redirectUrl);

        return true;
    }

    private isRootUrl(url: string): boolean {
        const path = url.split('?')[0].split('#')[0];

        return path === '' || path === '/';
    }

    private logRedirectRandomPercentage(randomPercentage: number): void {
        if (!environment.enableDebugMode) {
            return;
        }

        console.info('[StoreDataService] redirect randomPercentage', {
            randomPercentage,
        });
    }

    private getQueryString(url: string): string {
        const queryIndex = url.indexOf('?');

        if (queryIndex === -1) {
            return '';
        }

        return url.slice(queryIndex + 1);
    }

    private performExternalRedirect(url: string): void {
        window.location.replace(url);
    }

    storeInitialData() {
        const userInfo = this.retrieveUserInfo();
        this.storeCurrentData(userInfo).subscribe((res: Profile) => {
            const routerLink = {
                queryParams: {
                    profileId: res.profileId,
                    firstName: null,
                    lastName: null,
                    address: null,
                    city: null,
                    state: null,
                    suite: null,
                    email: null,
                    phone: null,
                    postalCode: null,
                    locationPhone: null,
                    locationState: null,
                    locationEmail: null,
                    locationName: null,
                    localSiteUrl: null,
                    country: null,
                    serviceTerritoryRegion: null,
                    serviceTerritoryID: null,
                    insertId: null,
                    offerCode: null,
                    websiteOfferName: null,
                    gclid: null,
                    msclkid: null,
                    utm_source: null,
                    utm_medium: null,
                    utm_campaign: null,
                    utm_term: null,
                    utm_matchtype: null,
                    gtc: null,
                    utm_content: null,
                    utm_adset: null,
                },
            };
            this.router.navigate([PAGES_URLS[PAGES.YOUR_CLEAN]], {
                queryParams: routerLink.queryParams,
                queryParamsHandling: 'merge',
            });
        });
    }

    retrieveUserInfo(): Profile {
        this.userInfo = {
            address: this.params['address'] || this.profileData['address'],
            localSiteUrl:
                this.params['localSiteUrl'] || this.profileData['localSiteUrl'],
            city: this.params['city'] || this.profileData['city'],
            state: this.params['state'] || this.profileData['state'],
            suite: this.params['suite'] || this.profileData['suite'],
            postalCode:
                this.params['postalCode'] || this.profileData['postalCode'],
            locationCode:
                this.params['locationCode'] || this.profileData['locationCode'],
            locationPhone:
                this.params['locationPhone'] ||
                this.profileData['locationPhone'],
            locationState:
                this.params['locationState'] ||
                this.profileData['locationState'],
            locationName:
                this.params['locationName'].replace(/merry maids of /i, '') ||
                this.profileData['locationName'],
            locationEmail:
                this.params['locationEmail'] ||
                this.profileData['locationEmail'],
            serviceTerritoryID:
                this.params['serviceTerritoryID'] ||
                this.profileData['serviceTerritoryID'],
            country: this.params['country'] || this.profileData['country'],
            serviceTerritoryRegion:
                this.params['serviceTerritoryRegion'] ||
                this.profileData['serviceTerritoryRegion'],
            initialUrl: this.initialUrl,
            insertId: this.params['insertId'] || this.profileData['insertId'],
            offerCode:
                this.params['offerCode'] || this.profileData['offerCode'],
            websiteOfferName:
                this.params['websiteOfferName'] || this.profileData['websiteOfferName'],
            gclid: this.params['gclid'] || this.profileData['gclid'],
            msclkid: this.params['msclkid'] || this.profileData['msclkid'],
            utmsource:
                this.params['utm_source'] || this.profileData['utmsource'],
            utmmedium:
                this.params['utm_medium'] || this.profileData['utmmedium'],
            utmcampaign:
                this.params['utm_campaign'] || this.profileData['utmcampaign'],
            utmterm: this.params['utm_term'] || this.profileData['utmterm'],
            utmmatchtype:
                this.params['utm_matchtype'] ||
                this.profileData['utmmatchtype'],
            gtc: this.params['gtc'] || this.profileData['gtc'],
            utmcontent: this.params['utm_content'] || this.profileData['utmcontent'],
            utmadset: this.params['utm_adset'] || this.profileData['utmadset'],
        };

        return this.userInfo;
    }

    getProfile(profileId: string | null) {
        this.http
            .get<Profile>(`${API_BASE_URL}/customer/${profileId}`)
            .subscribe({
                next: (profile: Profile) => {
                    if (!this.appStarted) {
                        this.profileData = profile;
                        this.appStarted = true;
                        if (this.continueBooking) {
                            if (profile.url)
                                this.routeToContinueBooking.next(profile.url);
                            this.continueBooking = false;
                        }
                    }
                },
                error: (e) => {
                    console.error(e);
                },
                complete: () => {
                    console.info('complete');
                },
            });
    }

    updateProfileData(profileData: Profile) {
        const profileDataUpdated = {
            ...this.profileData,
            ...profileData,
        };
        const isProfileChanged =
            JSON.stringify(Object.entries(this.storedProfile).sort()) !==
            JSON.stringify(Object.entries(profileDataUpdated).sort());
        if (isProfileChanged) {
            this.storeCurrentData(profileData, true, true).subscribe();
        }
    }

    storeCurrentData(
        profileData: Profile,
        skipSpinner?: boolean,
        skipUpdate?: boolean
    ) {
        const body = {
            ...this.profileData,
            ...profileData,
            leadVariant: 'Original',
        };
        this.storedProfile = JSON.parse(JSON.stringify(body));
        const context = new HttpContext().set(SKIP_SPINNER, skipSpinner);
        return this.http
            .put<Profile>(`${API_BASE_URL}/customer`, body, { context })
            .pipe(
                map((profile: Profile) => {
                    if (skipUpdate) {
                        profile.skipUpdate = true;
                    }
                    this.profileData = profile;
                    return this.profileData;
                })
            );
    }

    getCleanedDataAfterFlowChange() {
        this.profileData = {
            ...this.profileData,
            ...{
                cleaningOccasion: null,
                cleaningFrequency: null,
                square: null,
                url: null,
                petsNumber: null,
                levels: null,
                numberOfBedrooms: null,
                numberOfBathrooms: null,
                numberOfHalfbathrooms: null,
                otherRooms: null,
                rooms: null,
                concerns: null,
                scheduling: null,
                appliances: null,
                subscriptionType: null,
            },
        };
    }

    getProfileDataForQuote(
        selectedRooms: Room[],
        appliances: SelectedAppliance[],
        locationCode: number | null
    ): { bodyRequest: Profile; valid: boolean } {
        const bodyRequest = {
            locationCode,
            square: this.profileData.square,
            numberOfPets: this.profileData.petsNumber,
            rooms: selectedRooms,
            appliances: appliances,
        };
        const valid = this.isProfileDataValidForQuote(bodyRequest);
        return { bodyRequest, valid };
    }

    isProfileDataValidForQuote(profile: any) {
        for (const key in profile) {
            if (Object.prototype.hasOwnProperty.call(profile, key)) {
                if (this.validationPropertyExceptions.includes(key)) {
                    continue;
                }
                if (profile[key] === null) {
                    return false;
                }
            }
        }
        return true;
    }

    sendOrder(orderParameter?: OrderParameter) {
        return this.http.post<Profile>(
            `${API_BASE_URL}/lead/${
                this.profileData.profileId
            }/place?managerRequest=${orderParameter ? orderParameter : ''}`,
            {}
        );
    }
}
